import { apiService } from './api';

/**
 * Frontend client for the QA/QC Reference Materials catalog (OREAS / AMIS / custom MRCs).
 * Wraps the /reference-materials backend module added in Phase 1.
 */

export interface ReferenceMaterialValue {
  id?: string;
  reference_material_id?: string;
  analyte_symbol: string;
  analyte_name?: string | null;
  unit: string;
  method: string;
  certified_value?: number | null;
  sd_1?: number | null;
  ci_low_95?: number | null;
  ci_high_95?: number | null;
  is_indicative?: boolean;
  raw_value_text?: string | null;
}

export interface ReferenceMaterial {
  id: string;
  tenant_id: string | null;
  code: string;
  producer: string;
  name?: string | null;
  matrix?: string | null;
  mineralisation?: string | null;
  state?: string | null;
  description?: string | null;
  source_url?: string | null;
  is_active: boolean;
  last_synced_at?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  values?: ReferenceMaterialValue[];
}

export interface ReferenceMaterialListFilters {
  producer?: string;
  active?: boolean;
  search?: string;
}

export interface MethodCompatibilityDetail {
  method_code: string;
  oreas_digestion: string | null;
  has_values: boolean;
  missing_elements: string[];
}

export interface MethodCompatibilityResult {
  compatible: boolean;
  warnings: string[];
  details: MethodCompatibilityDetail[];
}

class ReferenceMaterialsService {
  private baseUrl = '/reference-materials';

  async list(filters: ReferenceMaterialListFilters = {}): Promise<ReferenceMaterial[]> {
    const params = new URLSearchParams();
    if (filters.producer) params.set('producer', filters.producer);
    if (filters.active !== undefined) params.set('active', String(filters.active));
    if (filters.search) params.set('search', filters.search);
    const qs = params.toString();
    return apiService.get<ReferenceMaterial[]>(qs ? `${this.baseUrl}?${qs}` : this.baseUrl);
  }

  async findOne(id: string): Promise<ReferenceMaterial> {
    return apiService.get<ReferenceMaterial>(`${this.baseUrl}/${id}`);
  }

  /**
   * Check whether an MRC has certified values for the chosen methods' digestions.
   * Powers the soft QA/QC warning at reception (before the lot is created).
   */
  async checkCompatibility(payload: {
    reference_material_id: string;
    method_codes: string[];
    elements?: string[];
  }): Promise<MethodCompatibilityResult> {
    return apiService.post<MethodCompatibilityResult>(
      `${this.baseUrl}/check-compatibility`,
      payload,
    );
  }

  async create(payload: Partial<ReferenceMaterial> & { values?: ReferenceMaterialValue[] }): Promise<ReferenceMaterial> {
    return apiService.post<ReferenceMaterial>(this.baseUrl, payload);
  }

  async importFromOreas(code: string, tenantScoped = false): Promise<ReferenceMaterial> {
    return apiService.post<ReferenceMaterial>(`${this.baseUrl}/import-oreas`, {
      code,
      tenant_scoped: tenantScoped,
    });
  }

  async listOreasCatalog(): Promise<{ codes: string[] }> {
    return apiService.get<{ codes: string[] }>(`${this.baseUrl}/oreas-catalog`);
  }

  async upsertValues(id: string, values: ReferenceMaterialValue[]): Promise<ReferenceMaterialValue[]> {
    return apiService.post<ReferenceMaterialValue[]>(`${this.baseUrl}/${id}/values`, values);
  }

  async update(id: string, payload: Partial<ReferenceMaterial>): Promise<ReferenceMaterial> {
    return apiService.patch<ReferenceMaterial>(`${this.baseUrl}/${id}`, payload);
  }

  async softDelete(id: string): Promise<{ ok: true }> {
    return apiService.delete<{ ok: true }>(`${this.baseUrl}/${id}`);
  }

  async deleteValue(id: string, valueId: string): Promise<{ ok: true }> {
    return apiService.delete<{ ok: true }>(`${this.baseUrl}/${id}/values/${valueId}`);
  }

  // -- Phase 4.3 — bulk OREAS sync ----------------------------------------

  async runOreasSync(): Promise<{ ok: true; log: OreasScrapeLog }> {
    return apiService.post<{ ok: true; log: OreasScrapeLog }>(
      `${this.baseUrl}/sync/run`,
      {},
    );
  }

  async listOreasSyncLogs(limit = 30): Promise<OreasScrapeLog[]> {
    return apiService.get<OreasScrapeLog[]>(`${this.baseUrl}/sync/logs?limit=${limit}`);
  }
}

export interface OreasScrapeLogCodeDiff {
  code: string;
  status: 'added' | 'updated' | 'unchanged' | 'failed';
  added: number;
  updated: number;
  unchanged: number;
  message?: string;
}

export interface OreasScrapeLog {
  id: string;
  trigger: 'cron' | 'manual';
  triggered_by?: string | null;
  started_at: string;
  finished_at?: string | null;
  total_codes: number;
  added_codes: number;
  updated_codes: number;
  unchanged_codes: number;
  failed_codes: number;
  summary?: { codes: OreasScrapeLogCodeDiff[] };
  error?: string | null;
}

export const referenceMaterialsService = new ReferenceMaterialsService();
export default referenceMaterialsService;
